import React from 'react';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';

export interface MmePickItem {
  id: string;
  title: string;
  hint?: string;
  badge?: number;
  tourId?: string;
  icon?: LucideIcon;
}

/** Large choices, one line each. The simple-home pattern for tabs. */
export const MmePickList: React.FC<{
  items: MmePickItem[];
  onPick: (id: string) => void;
}> = ({ items, onPick }) => {
  return (
    <div className="grid grid-cols-1 gap-2 p-2 sm:grid-cols-2 sm:p-3">
      {items.map((item) => {
        const Icon = item.icon;
        return (
        <button
          key={item.id}
          type="button"
          data-tour-id={item.tourId}
          onClick={() => onPick(item.id)}
          className="flex min-h-14 items-center justify-between gap-3 rounded-2xl border border-[#eadfd2] bg-[#fffdf8] px-4 text-start dark:border-[#3d342c] dark:bg-[#261f1b]"
        >
          <span className="flex min-w-0 items-center gap-3">
            {Icon ? <Icon className="h-5 w-5 shrink-0 text-[#8a6a45] dark:text-[#d4b48a]" aria-hidden /> : null}
            <span className="min-w-0">
            <span className="block truncate text-base font-semibold text-[#2b221b] dark:text-[#f6efe6]">{item.title}</span>
            {item.hint ? (
              <span className="mt-0.5 block truncate text-sm text-[#7a6a5c] dark:text-[#cbbba8]">{item.hint}</span>
            ) : null}
            </span>
          </span>
          {item.badge && item.badge > 0 ? (
            <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-[#3f2a56] px-1.5 text-xs font-semibold text-[#fff8ee]">
              {item.badge > 9 ? '9+' : item.badge}
            </span>
          ) : null}
        </button>
        );
      })}
    </div>
  );
};

export const MmePickBack: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';

  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex min-h-8 items-center gap-1 px-2 text-sm font-semibold text-[#3f2a56] dark:text-[#f0d7b0]"
    >
      <ChevronRight className={`h-4 w-4 ${isRtl ? '' : 'rotate-180'}`} aria-hidden />
      {t('mmeBack')}
    </button>
  );
};
